import { startCase } from "lodash";
import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";

import { ErrorDisplayUnsavedItems } from "@ryde/controls";
import { useConfirmDirtyField, useCrudEditContext } from "@ryde/redux";
import {
  courseHasUnsavedStationChangeAtom,
  GlobalAppEventEnum,
  ICourseDto,
  IDrawersCommonProps,
  IExtendableDto,
  IInjectedDrawerProps,
  isStaleEditFormDto,
  KeyboardKeys,
  loadersEditFormWithApiDataSF,
  RIGHTBAR_COURSES_WIDTH,
  SimpleResult,
  useIsMobile,
} from "@ryde/shared";

import { BaseDrawer, useFormMapDrawerResize } from "../../../drawers";
import { LayoutRightLoader } from "../../../right";
import { CourseFooter } from "./course.footer";
import { CourseHeader } from "./course.header";
import { CourseMap } from "./course.map";
import { CourseDrawerWrapper } from "./course.styled";
import { CourseTabsWrapper } from "./course.tabs.wrapper";

interface ICourseEditContentProps {
  /** Draft opened with values the caller already filled in — Save must not wait for a user edit. */
  hasPrefilledDraft?: boolean;
}

export const CourseEditContent: FC<
  IDrawersCommonProps & IInjectedDrawerProps & ICourseEditContentProps
> = ({ onCloseDrawer, resolveFn, drawerNumber, hasPrefilledDraft }) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const {
    entity,
    mode,
    id,
    data: { dto },
  } = useCrudEditContext<ICourseDto>();
  const {
    formState: { isDirty },
  } = useFormContext<Partial<ICourseDto>>();

  const loadingCrudData = useRecoilValue(loadersEditFormWithApiDataSF(entity));
  const isCourseDtoStale = isStaleEditFormDto({
    requestedId: id,
    dtoId: dto?.id,
    hasDto: Boolean(dto),
  });
  const isCourseDataLoading = id
    ? loadingCrudData || isCourseDtoStale
    : loadingCrudData;
  const isCourseContentDisplayed = id
    ? !isCourseDataLoading && dto?.id === id
    : !isCourseDataLoading;
  const [courseHasUnsavedStationChange, setCourseHasUnsavedStationChange] =
    useRecoilState(courseHasUnsavedStationChangeAtom);
  const isCourseDirty =
    isDirty || courseHasUnsavedStationChange || Boolean(hasPrefilledDraft);

  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems />,
  });

  const unitTitle = t(`${entity}.single`);

  const onCloseClick = applyIfConfirmed({ isDirty: isCourseDirty })(
    (): void => {
      setCourseHasUnsavedStationChange(false);
      resolveFn?.(new SimpleResult<IExtendableDto>({ success: false }));
      onCloseDrawer();
    },
  );

  useEffect(() => {
    const closeOnEscapePressed = (e: KeyboardEvent): void => {
      if (e.key === KeyboardKeys.ESCAPE) {
        onCloseClick();
      }
    };

    window.addEventListener("keydown", closeOnEscapePressed);
    return () => window.removeEventListener("keydown", closeOnEscapePressed);
  }, [onCloseClick]);

  const {
    gridStyle,
    drawerWidthStyle,
    isMapVisible,
    onFormHandleMouseDown,
    onFormHandleKeyDown,
    onMapHandleMouseDown,
    onMapHandleKeyDown,
  } = useFormMapDrawerResize({
    drawerNumber,
    mapResizeEvent: GlobalAppEventEnum.RESIZE_COURSE_MAP,
  });

  return (
    <BaseDrawer
      onCloseDrawer={onCloseClick}
      drawerNumber={drawerNumber}
      customWidth={isMobile ? RIGHTBAR_COURSES_WIDTH : drawerWidthStyle}
    >
      {isCourseContentDisplayed && (
        <CourseDrawerWrapper style={gridStyle}>
          <CourseHeader
            headerTitle={startCase(unitTitle)}
            closeClick={onCloseClick}
            drawerNumber={drawerNumber}
          />
          {!isMobile && (
            // eslint-disable-next-line jsx-a11y-x/no-noninteractive-element-interactions -- intentional: keyboard-operable resize handle
            <div
              className="CourseDrawer-LeftHandle"
              onMouseDown={onFormHandleMouseDown}
              onKeyDown={onFormHandleKeyDown}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize form section"
              // eslint-disable-next-line jsx-a11y-x/no-noninteractive-tabindex -- intentional: focusable resize handle operated via onKeyDown
              tabIndex={0}
            />
          )}
          <CourseTabsWrapper />
          <CourseFooter
            onCloseDrawer={onCloseDrawer}
            isCourseDirty={isCourseDirty}
            resolveFn={resolveFn}
            hasPrefilledDraft={hasPrefilledDraft}
          />
          {!isMobile && (
            // eslint-disable-next-line jsx-a11y-x/no-noninteractive-element-interactions -- intentional: keyboard-operable resize handle
            <div
              className="CourseDrawer-MiddleHandle"
              onMouseDown={onMapHandleMouseDown}
              onKeyDown={onMapHandleKeyDown}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize map section"
              // eslint-disable-next-line jsx-a11y-x/no-noninteractive-tabindex -- intentional: focusable resize handle operated via onKeyDown
              tabIndex={0}
            />
          )}
          {!isMobile && isMapVisible && <CourseMap mode={mode} />}
        </CourseDrawerWrapper>
      )}
      {isCourseDataLoading && (
        <LayoutRightLoader>{t(`ui.LOADING_DATA`)}</LayoutRightLoader>
      )}
    </BaseDrawer>
  );
};
